import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Layout from './CoreComponents/Layout';
import ConfirmationModal from './Shared/ConfirmationModal';
import ProjectEditView from './Shared/ProjectEditViewModal';
import ItemView from './Shared/ItemView';
import DateInput from './Shared/DateInput';
import BaseGrid from './Shared/BaseGrid';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { createPORequest, getPORequests, deletePORequest, updatePORequest, setPORequestPendingByUser, getStatusCounts, type PORequestQueryParams } from '../store/slices/poRequestSlice';
import { getItemsWithPagination, createItem, uploadItemImages, getItemById, type Item, resetPagination } from '../store/slices/itemSlice';
import { getItemCategories } from '../store/slices/itemCategorySlice';
// import { uploadPoRequestAttachments } from '../store/slices/poRequestAttachmentSlice'; // Removed
import { 
  formatDateWithMoment, 
  convertToInputFormat,
  getCurrentDateForInput,
  getDatePlaceholder as getMomentDatePlaceholder
} from '../utils/momentDateFormatter';
import { type ColDef, type ICellRendererParams, type RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AppDispatch, RootState } from '../store/index';

interface CreateRequestPageProps {}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object' && error !== null) {
    if ('message' in error) {
      return String((error as { message: unknown }).message);
    } else if ('response' in error && error.response && typeof error.response === 'object' && error.response !== null && 'data' in error.response) {
      return String((error.response as { data: unknown }).data);
    } else if ('status' in error) {
      return `HTTP ${(error as { status: unknown }).status}: Unknown error`;
    }
  }
  return 'Bilinmeyen bir hata oluştu';
}

interface CartItem {
  id?: number;
  itemCode: string;
  shortCode?: string;
  itemName: string;
  requestedQuantity: number;
  unit?: string;
  requestedDeliveryDate: string;
  // projectDesciption: string; // Removed - not in PORequestDto
  isUrgent: boolean;
  attachmentUrl?: string;
  moq?: number;
}

const CreateRequestPage: React.FC<CreateRequestPageProps> = () => {
  const { user } = useUser();
  const { showSuccess, showError, showWarning } = useToast();
  const dispatch = useDispatch<AppDispatch>();

  // Redux state'ini oku - sadece loading state'i
  const { loading: isLoading } = useSelector((state: RootState) => state.poRequest);
  
  // Local state for fresh data - cache'leme yok
  const [poRequests, setPoRequests] = useState<any[]>([]);
  const [isSendingToEvaluation, setIsSendingToEvaluation] = useState(false);
  const { items: allItems, loading: itemsLoading, selectedItem, pagination } = useSelector((state: RootState) => state.item);
  const { categories: itemCategories, loading: categoriesLoading } = useSelector((state: RootState) => state.itemCategory);
  


  // Veri formatla
  const formatCellValue = useCallback((value: unknown, type: string): string => {
    if (value === null || value === undefined) return '-';

    switch (type) {
      case 'date':
        return formatDateWithMoment(value as string | Date);
      case 'number': {
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return numValue.toLocaleString('tr-TR');
      }
      case 'file':
        return value ? '📎' : '-';
      case 'actions':
        return ''; // Actions için boş string döndür
      case 'text':
      default:
        return String(value);
    }
  }, []);
  
  // Local states
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProjectEditModal, setShowProjectEditModal] = useState(false);
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [itemToEdit, setItemToEdit] = useState<any>(null);
  const [projectToEdit, setProjectToEdit] = useState<any>(null);
  const [itemToView, setItemToView] = useState<any>(null);
  const [confirmationType, setConfirmationType] = useState<'delete' | 'deleteRequest' | 'deleteAllRequests' | 'sendToEvaluation'>('delete');
  
  // Product modal states
  const [selectedProduct, setSelectedProduct] = useState<Item | null>(null);
  const [productModalForm, setProductModalForm] = useState({
    quantity: 1,
    deliveryDate: '',
    searchTerm: '',
    selectedCategoryId: null as number | null,
    note: ''
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const productModalGridRef = useRef<AgGridReact<Item>>(null);
  const productSearchInputRef = useRef<HTMLInputElement>(null);
  const editModalQuantityInputRef = useRef<HTMLInputElement>(null);

  
  
  // Cart state
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(1);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  
  // Selection state - removed individual selection

  // Project modal states
  const [projectModalForm, setProjectModalForm] = useState({
    attachments: [] as File[],
    deliveryDate: '',
    quantity: 1
  });

  // Edit modal states
  const [editModalForm, setEditModalForm] = useState({
    quantity: 1,
    deliveryDate: '',
    note: ''
  });
  const [projectEditDeliveryDate, setProjectEditDeliveryDate] = useState<string>('');

  const fetchPORequests = useCallback(async () => {
    try {
      const requestParams: PORequestQueryParams & { _t?: number } = {
        RequestStatus: 'Draft',
        _t: Date.now() // Cache busting - her seferinde farklı timestamp
      };
      
      if (user?.id) {
        requestParams.CreatedBy = user.id;
      }
      
      const result = await dispatch(getPORequests(requestParams)).unwrap();
      
      // API'den gelen fresh data'yı local state'e set et - cache yok
      if (result.response && typeof result.response === 'object' && 'success' in result.response) {
        if (result.response.success && result.response.data) {
          setPoRequests(result.response.data);
        } else {
          showError(result.response.message || 'Talepler yüklenemedi');
          setPoRequests([]);
        }
      } else if (Array.isArray(result.response)) {
        setPoRequests(result.response);
      } else {
        setPoRequests([]);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(`Talepler yüklenirken hata oluştu: ${errorMessage}`);
      setPoRequests([]);
    }
  }, [dispatch, user?.id, showError]);

  // Fetch PORequests on component mount
  useEffect(() => {
    // localStorage'dan token'ı al
    const token = localStorage.getItem('authToken');
    
    if (token) {
      fetchPORequests();
    }
  }, [fetchPORequests]);

  // Modal açıldığında her seferinde fresh data çek
  useEffect(() => {
    if (showProductModal) {
      // Reset pagination and load first page
      dispatch(resetPagination());
      setCurrentPage(1);
      
      // Kategorileri ve ürünleri çek
      dispatch(getItemCategories({}));
      dispatch(getItemsWithPagination({ 
        Page: 1, 
        PageSize: 50
      }));
    }
  }, [showProductModal, dispatch]);

  // Modal açıldığında arama çubuğuna focus ver
  useEffect(() => {
    if (showProductModal && productSearchInputRef.current) {
      // Kısa bir gecikme ile focus ver (modal animasyonu için)
      setTimeout(() => {
        productSearchInputRef.current?.focus();
      }, 100);
    }
  }, [showProductModal]);

  // Edit modal açıldığında miktar alanına focus ver
  useEffect(() => {
    if (showEditModal && editModalQuantityInputRef.current) {
      // Kısa bir gecikme ile focus ver (modal animasyonu için)
      setTimeout(() => {
        editModalQuantityInputRef.current?.focus();
      }, 100);
    }
  }, [showEditModal]);

  // Proje modal açıldığında kategorileri çek
  useEffect(() => {
    if (showProjectModal) {
      dispatch(getItemCategories({}));
    }
  }, [showProjectModal, dispatch]);

  // Project edit modal açıldığında endpoint çağırma - modal kendi içinde gerekli verileri çekiyor

  // Backend'den searchTerm ve category ile filtrelenmiş veri geliyor
  // useEffect(() => {
  //   if (showProductModal && allItems.length > 0) {
  //     applyFilters();
  //   }
  // }, [allItems, showProductModal, searchTerm, selectedCategoryId, applyFilters]); // Moved after applyFilters definition

  // Modal functions
  const openProductModal = () => {
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    resetProductModalForm();
  };



  // Column filter: client-side filter on allItems
  const handleSearch = async () => {
    dispatch(resetPagination());
    setCurrentPage(1);
    const requestParams: any = { Page: 1, PageSize: 50 };
    if (productModalForm.selectedCategoryId !== null) {
      requestParams.CategoryId = productModalForm.selectedCategoryId;
    }
    if (productModalForm.searchTerm) {
      requestParams.SearchKeyword = productModalForm.searchTerm;
    }
    try {
      await dispatch(getItemsWithPagination(requestParams)).unwrap();
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleProductSelect = useCallback((item: Item) => {
    setSelectedProduct(item);
  }, []);

  // Load more items function
  const loadMoreItems = useCallback(async () => {
    if (isLoadingMore || !pagination.hasMore) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const requestParams: any = { Page: nextPage, PageSize: 50 };
      if (productModalForm.selectedCategoryId !== null) {
        requestParams.CategoryId = productModalForm.selectedCategoryId;
      }
      if (productModalForm.searchTerm) {
        requestParams.SearchKeyword = productModalForm.searchTerm;
      }
      await dispatch(getItemsWithPagination(requestParams)).unwrap();
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, currentPage, pagination.hasMore, isLoadingMore, productModalForm.selectedCategoryId, productModalForm.searchTerm]);

  const addProductFromModal = async () => {
    if (!selectedProduct || !productModalForm.quantity || !productModalForm.deliveryDate) {
      showWarning('Lütfen bir ürün seçin, miktar ve teslim tarihi girin!');
      return;
    }

    // Check if quantity is valid (must be greater than 0)
    if (productModalForm.quantity <= 0) {
      showWarning('Miktar 0\'dan büyük olmalıdır!');
      return;
    }

    // Check if quantity meets MOQ requirement
    if (selectedProduct.moq && productModalForm.quantity < selectedProduct.moq) {
      showWarning(`Miktar, bu ürünün minimum sipariş miktarından (${selectedProduct.moq}) az olamaz!`);
      return;
    }

    // Check if delivery date is in the past using moment.js
    const today = getCurrentDateForInput();
    if (productModalForm.deliveryDate < today) {
      showWarning('Teslim tarihi geçmiş bir tarih olamaz!');
      return;
    }

    try {
      // Create PORequest for the selected product
      const requestData = {
        id: 0,
        itemId: selectedProduct.id || 0,
        itemCode: selectedProduct.itemCode || null,
        shortCode: selectedProduct.shortCode || null,
        itemName: selectedProduct.itemName || null,
        categoryId: selectedProduct.categoryId || 0,
        categoryName: selectedProduct.categoryName || null,
        // projectDesciption: null, // Removed - not in PORequestDto
        attachmentUrl: null,
        requestedQuantity: productModalForm.quantity,
        requestedDeliveryDate: new Date(productModalForm.deliveryDate),
        isUrgent: false,
        requestStatus: 'draft',
        approvedQuantity: 0,
        Note: productModalForm.note || ''
      };

      const response = await dispatch(createPORequest(requestData)).unwrap();
      
      if (response.success) {
        showSuccess('Ürün talebi başarıyla oluşturuldu!');
      } else {
        showError(response.message || 'Talep oluşturulurken hata oluştu');
      }
      
      // Close modal
      closeProductModal();
      
      // Refresh the PORequests list - fresh data
      await fetchPORequests();
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
      
    } catch (error) {
      console.error('addProductFromModal error:', error);
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  const addProductAndContinue = async () => {
    if (!selectedProduct || !productModalForm.quantity || !productModalForm.deliveryDate) {
      showWarning('Lütfen bir ürün seçin, miktar ve teslim tarihi girin!');
      return;
    }

    if (productModalForm.quantity <= 0) {
      showWarning('Miktar 0\'dan büyük olmalıdır!');
      return;
    }

    if (selectedProduct.moq && productModalForm.quantity < selectedProduct.moq) {
      showWarning(`Miktar, bu ürünün minimum sipariş miktarından (${selectedProduct.moq}) az olamaz!`);
      return;
    }

    const today = getCurrentDateForInput();
    if (productModalForm.deliveryDate < today) {
      showWarning('Teslim tarihi geçmiş bir tarih olamaz!');
      return;
    }

    try {
      const requestData = {
        id: 0,
        itemId: selectedProduct.id || 0,
        itemCode: selectedProduct.itemCode || null,
        shortCode: selectedProduct.shortCode || null,
        itemName: selectedProduct.itemName || null,
        categoryId: selectedProduct.categoryId || 0,
        categoryName: selectedProduct.categoryName || null,
        attachmentUrl: null,
        requestedQuantity: productModalForm.quantity,
        requestedDeliveryDate: new Date(productModalForm.deliveryDate),
        isUrgent: false,
        requestStatus: 'draft',
        approvedQuantity: 0,
        Note: productModalForm.note || ''
      };

      const response = await dispatch(createPORequest(requestData)).unwrap();

      if (response.success) {
        showSuccess('Ürün eklendi, devam edebilirsiniz.');
      } else {
        showError(response.message || 'Talep oluşturulurken hata oluştu');
        return;
      }

      setSelectedProduct(null);
      setProductModalForm(prev => ({ ...prev, quantity: 1, deliveryDate: '', note: '' }));

      fetchPORequests();
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
    } catch (error) {
      console.error('addProductAndContinue error:', error);
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  const resetProductModalForm = () => {
    setSelectedProduct(null);
    setProductModalForm({
      quantity: 1,
      deliveryDate: '',
      searchTerm: '',
      selectedCategoryId: null,
      note: ''
    });
    setCurrentPage(1);
    setIsLoadingMore(false);
  };

  // Project modal functions
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setProjectModalForm(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles]
      }));
    }
  };

  const removeAttachment = (index: number) => {
    setProjectModalForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const resetProjectModalForm = () => {
    setProjectModalForm({
      attachments: [],
      deliveryDate: '',
      quantity: 1
    });
    // Reset all form inputs
    const form = document.querySelector('form');
    if (form) {
      form.reset();
    }
  };

  // Project submit handler
  const handleProjectSubmit = async () => {
    try {
      // Get form data
      const form = document.querySelector('form');
      if (!form) {
        showError('Form bulunamadı!');
        return;
      }

      const projectTitle = (form.querySelector('input[type="text"]') as HTMLInputElement)?.value;
      const projectDetails = (form.querySelector('textarea') as HTMLTextAreaElement)?.value;
      const projectType = 'Proje'; // Always set to 'Proje'
      const projectCategorySelect = (form.querySelector('select') as HTMLSelectElement);
      const projectCategory = projectCategorySelect?.value;
      const projectCategoryName = projectCategorySelect?.selectedOptions[0]?.text || 'Proje';
      const numberInputs = form.querySelectorAll('input[type="number"]') as NodeListOf<HTMLInputElement>;
      const projectQuantity = numberInputs[0]?.value; // First number input is quantity
      const unit = (form.querySelector('input[placeholder*="Birim giriniz"]') as HTMLInputElement)?.value;
      const vatRateInput = form.querySelector('input[name="vatRate"]') as HTMLInputElement;
      const vatRate = vatRateInput?.value || '0';
      const deliveryDate = projectModalForm.deliveryDate;

      // Validation
      if (!projectTitle || projectTitle.trim() === '') {
        showError('Proje başlığı boş bırakılamaz!');
        return;
      }

      if (!projectQuantity || parseInt(projectQuantity) <= 0) {
        showError('Miktar 0\'dan büyük olmalıdır!');
        return;
      }

      // Check if quantity meets MOQ requirement (for projects, MOQ is typically 1)
      const projectMOQ = 1; // Projects typically have MOQ of 1
      if (parseInt(projectQuantity) < projectMOQ) {
        showError(`Miktar, proje için minimum sipariş miktarından (${projectMOQ}) az olamaz!`);
        return;
      }

      if (!deliveryDate || deliveryDate.trim() === '') {
        showError('Teslim tarihi boş bırakılamaz!');
        return;
      }

      // Check if delivery date is in the past using moment.js
      const today = getCurrentDateForInput();
      if (deliveryDate < today) {
        showError('Teslim tarihi geçmiş bir tarih olamaz!');
        return;
      }

      // Create ItemDto for the first endpoint
      const itemData = {
        
          "id": 0,
          "itemCode": '',
          "shortCode": 'PRJ',
          "itemName": projectTitle,
          "description": projectDetails,
          "categoryId": projectCategory ? parseInt(projectCategory) : 0,
          "categoryName": projectCategoryName,
          "itemType": projectType,
          "unit":unit || '',
          "vatRate": parseFloat((vatRate || '0').replace('%', '')),
          "inActive": false,
          "createdAt": new Date(),
          "createdBy": user?.id || 0,
          "updatedAt": new Date(),
          "updatedBy": 0
      };


      // First API call - Create Item
      const createItemResult = await dispatch(createItem(itemData)).unwrap();
      
      if (createItemResult && createItemResult.success) {
        // Get the created item data from the response
        const createdItem = createItemResult.data;
        const createdItemId = createdItem?.id;
        
        // Check if user uploaded files and upload them
        if (projectModalForm.attachments.length > 0) {
          try {
            if (createdItemId) {
              // Upload files to the created item
              await dispatch(uploadItemImages({ 
                itemId: createdItemId, 
                files: projectModalForm.attachments 
              })).unwrap();
            } else {
              showWarning('Proje oluşturuldu ancak dosya yüklenemedi (ID bulunamadı)');
            }
          } catch (uploadError) {
            console.error('File upload error:', uploadError);
            const errorMessage = getErrorMessage(uploadError);
            showWarning(`Proje oluşturuldu ancak dosyalar yüklenemedi: ${errorMessage}`);
          }
        }
        
        // Create PORequest for the created item
        if (createdItemId) {
          try {
            const requestData = {
              id: 0,
              itemId: createdItemId,
              itemCode: createdItem?.itemCode || null,
              shortCode: createdItem?.shortCode || null,
              itemName: createdItem?.itemName || null,
              categoryId: createdItem?.categoryId || 0,
              categoryName: createdItem?.categoryName || null,
              attachmentUrl: null,
              requestedQuantity: projectQuantity ? parseInt(projectQuantity) : 1,
              requestedDeliveryDate: new Date(deliveryDate),
              isUrgent: false,
              requestStatus: 'draft',
              approvedQuantity: 0,
              vatRate: parseFloat(vatRate)
            };

            console.log('==== CREATE PO REQUEST BODY ====');
            console.log(JSON.stringify(requestData, null, 2));
            console.log('================================');

            await dispatch(createPORequest(requestData)).unwrap();
            showSuccess('Proje talebi başarıyla oluşturuldu!');
          } catch (poRequestError) {
            console.error('PORequest creation error:', poRequestError);
            const errorMessage = getErrorMessage(poRequestError);
            showWarning(`Proje oluşturuldu ancak talep oluşturulamadı: ${errorMessage}`);
          }
        } else {
          showWarning('Proje oluşturuldu ancak talep oluşturulamadı (ID bulunamadı)');
        }
        
        // Close modal and reset form after all operations complete
        setShowProjectModal(false);
        resetProjectModalForm();
        
        // Refresh the PORequests list - fresh data
        await fetchPORequests();
        if (user?.id) {
          dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
        }
      } else {
        showError(createItemResult?.message || 'Proje oluşturulurken hata oluştu!');
      }

    } catch (error) {
      console.error('Project submit error:', error);
      const errorMessage = getErrorMessage(error);
      showError(`Proje oluşturulurken hata oluştu: ${errorMessage}`);
    }
  };



  // Cart functions
  const removeFromCart = (index: number) => {
    setItemToDelete(index);
    setConfirmationType('delete');
    setShowConfirmationModal(true);
  };

  const startEditItem = (item: CartItem, index: number) => {
    setEditingItemId(index);
    setEditQuantity(item.requestedQuantity);
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
    setEditQuantity(1);
  };

  const saveEditItem = () => {
    if (editQuantity <= 0) {
      showWarning('Miktar 0\'dan büyük olmalıdır!');
      return;
    }

    // Get the current item being edited
    const currentItem = cartItems[editingItemId!];
    if (currentItem && currentItem.moq && editQuantity < currentItem.moq) {
      showWarning(`Miktar, bu ürünün minimum sipariş miktarından (${currentItem.moq}) az olamaz!`);
      return;
    }

    setCartItems(cartItems.map((item, index) => 
      index === editingItemId 
        ? { ...item, requestedQuantity: editQuantity }
        : item
    ));
    setEditingItemId(null);
    setEditQuantity(1);
  };


  const confirmAction = () => {
    if (confirmationType === 'delete' && itemToDelete !== null) {
      setCartItems(cartItems.filter((_, index) => index !== itemToDelete));
      setItemToDelete(null);
    } else if (confirmationType === 'deleteRequest') {
      confirmDeleteRequest();
    } else if (confirmationType === 'deleteAllRequests') {
      confirmDeleteAllRequests();
    } else if (confirmationType === 'sendToEvaluation') {
      handleSendToEvaluation();
    }
    setShowConfirmationModal(false);
  };

  const cancelAction = () => {
    setItemToDelete(null);
    setShowConfirmationModal(false);
  };

  // Edit modal functions
  const openEditModal = useCallback(async (item: any) => {
    // Check if item is a PRJ project
    if (item.itemCode?.startsWith('PRJ')) {
      setProjectToEdit(item);

      // Format the date for project edit modal - same logic as product edit
      if (item.requestedDeliveryDate) {
        const formattedDate = convertToInputFormat(item.requestedDeliveryDate);
        setProjectEditDeliveryDate(formattedDate);
      } else {
        setProjectEditDeliveryDate('');
      }
      setShowProjectEditModal(true);
    } else {
      setItemToEdit(item);
      setEditModalForm({
        quantity: item.requestedQuantity || 1,
        deliveryDate: item.requestedDeliveryDate ? convertToInputFormat(item.requestedDeliveryDate) : '',
        note: item.note ?? item.Note ?? ''
      });

      // getItemById endpoint'ini çağır
      if (item.itemId) {
        try {
          await dispatch(getItemById(item.itemId)).unwrap();
        } catch (error) {
          console.error('getItemById error:', error);
          const errorMessage = getErrorMessage(error);
          showError(`Ürün detayları yüklenirken hata oluştu: ${errorMessage}`);
        }
      }

      setShowEditModal(true);
    }
  }, [dispatch, showError]);


  const closeEditModal = () => {
    setShowEditModal(false);
    setItemToEdit(null);
    setEditModalForm({
      quantity: 1,
      deliveryDate: '',
      note: ''
    });
  };

  const closeProjectEditModal = () => {
    setShowProjectEditModal(false);
    setProjectToEdit(null);
  };

  // ItemView modal functions
  const openItemView = useCallback((item: any) => {
    setItemToView(item);
    setShowItemViewModal(true);
  }, []);

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };



  const handleEditSubmit = async () => {
    if (!itemToEdit || !editModalForm.quantity || !editModalForm.deliveryDate) {
      showWarning('Lütfen tüm alanları doldurun!');
      return;
    }

    // Check if quantity is valid (must be greater than 0)
    if (editModalForm.quantity <= 0) {
      showWarning('Miktar 0\'dan büyük olmalıdır!');
      return;
    }

    // Check if quantity meets MOQ requirement
    const currentMoq = selectedItem?.moq || itemToEdit.moq;
    if (currentMoq && editModalForm.quantity < currentMoq) {
      showWarning(`Miktar, bu ürünün minimum sipariş miktarından (${currentMoq}) az olamaz!`);
      return;
    }

    // Check if delivery date is in the past using moment.js
    const today = getCurrentDateForInput();
    if (editModalForm.deliveryDate < today) {
      showWarning('Teslim tarihi geçmiş bir tarih olamaz!');
      return;
    }

    try {
      // Create updated PORequest data
      const requestData = {
        id: itemToEdit.id || 0,
        itemId: itemToEdit.itemId || 0,
        itemCode: itemToEdit.itemCode || null,
        shortCode: itemToEdit.shortCode || null,
        itemName: itemToEdit.itemName || null,
        categoryId: itemToEdit.categoryId || 0,
        categoryName: itemToEdit.categoryName || null,
        // projectDesciption: itemToEdit.projectDesciption || null, // Removed - not in PORequestDto
        attachmentUrl: itemToEdit.attachmentUrl || null,
        requestedQuantity: editModalForm.quantity,
        requestedDeliveryDate: new Date(editModalForm.deliveryDate),
        isUrgent: false,
        requestStatus: itemToEdit.requestStatus || null,
        approvedQuantity: itemToEdit.approvedQuantity || 0,
        Note: editModalForm.note || ''
      };

      // Call the API to update PORequest
      const result = await dispatch(updatePORequest({ id: itemToEdit.id!, requestData })).unwrap();
      
      // The response is now wrapped in { id, response }
      if (result.response && typeof result.response === 'object' && 'success' in result.response) {
        if (result.response.success) {
          showSuccess('Talep başarıyla güncellendi!');
        } else {
          showError(result.response.message || 'Talep güncellenirken hata oluştu');
        }
      } else {
        showSuccess('Talep başarıyla güncellendi!');
      }
      
      // Close modal
      closeEditModal();
      
      // Refresh the PORequests list - fresh data
      await fetchPORequests();
      
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  // Delete request function
  const handleDeleteRequest = useCallback((id: number) => {
    setItemToDelete(id);
    setConfirmationType('deleteRequest');
    setShowConfirmationModal(true);
  }, []);


  const confirmDeleteRequest = async () => {
    if (itemToDelete === null) return;

    try {
      await dispatch(deletePORequest(itemToDelete)).unwrap();
      showSuccess('Talep başarıyla silindi!');
      
      // Fresh data çek - cache yok
      await fetchPORequests();
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  const confirmDeleteAllRequests = async () => {
    try {
      // Tüm talepleri sil
      const deletePromises = poRequests.map(request => 
        dispatch(deletePORequest(request.id!))
      );
      
      await Promise.all(deletePromises);
      showSuccess('Tüm talepler başarıyla silindi!');
      
      // Fresh data çek - cache yok
      await fetchPORequests();
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  const submitRequest = async () => {
    if (cartItems.length === 0) {
      showWarning('Sepetiniz boş! Lütfen önce ürün ekleyin.');
      return;
    }

    // Validate MOQ for all cart items
    for (const item of cartItems) {
      if (item.moq && item.requestedQuantity < item.moq) {
        showWarning(`${item.itemName} ürünü için miktar, minimum sipariş miktarından (${item.moq}) az olamaz!`);
        return;
      }
    }

    try {
      // Create PORequests for each cart item
      const promises = cartItems.map(async (item) => {
        const requestData = {
          id: 0,
          itemId: 0,
          itemCode: item.itemCode,
          shortCode: item.shortCode || null,
          itemName: item.itemName,
          categoryId: 0,
          categoryName: null,
          // projectDesciption: item.projectDesciption, // Removed - not in PORequestDto
          attachmentUrl: item.attachmentUrl || null,
          requestedQuantity: item.requestedQuantity,
          requestedDeliveryDate: new Date(item.requestedDeliveryDate),
          isUrgent: item.isUrgent,
          requestStatus: 'draft',
          approvedQuantity: 0
        };

        const response = await dispatch(createPORequest(requestData)).unwrap();
        if (response.success) {
          showSuccess('Talep başarıyla gönderildi!');
        } else {
          showError(response.message);
        }
        return response;
      });

      await Promise.all(promises);
      
      showSuccess('Talepler başarıyla gönderildi!');
      setCartItems([]);
      
      // Refresh the PORequests list - fresh data
      await fetchPORequests();
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
    }
  };

  // Handle send to evaluation
  const handleSendToEvaluation = async () => {
    if (poRequests.length === 0) {
      showWarning('Değerlendirmeye gönderilecek talep bulunamadı!');
      return;
    }

    if (!user?.id) {
      showError('Kullanıcı bilgisi bulunamadı!');
      return;
    }

    setIsSendingToEvaluation(true);
    try {
      // Yeni endpoint kullan: setPORequestPendingByUser - kullanıcının tüm taleplerini beklemede durumuna al
      const result = await dispatch(setPORequestPendingByUser(user.id)).unwrap();
      
      if (result) {
        showSuccess(`${poRequests.length} talep başarıyla değerlendirmeye gönderildi!`);
        
        // Listeyi yenile - fresh data
        await fetchPORequests();
        if (user?.id) {
          dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
        }
      } else {
        showError('Talepler değerlendirmeye gönderilemedi!');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(`Değerlendirmeye gönderilirken hata oluştu: ${errorMessage}`);
    } finally {
      setIsSendingToEvaluation(false);
    }
  };

  // Show confirmation for send to evaluation
  const showSendToEvaluationConfirmation = () => {
    if (poRequests.length === 0) {
      showWarning('Değerlendirmeye gönderilecek talep bulunamadı!');
      return;
    }
    setConfirmationType('sendToEvaluation');
    setShowConfirmationModal(true);
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Talep No',
      field: 'id',
      minWidth: 88,
      flex: 0.6,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Kodu',
      field: 'itemCode',
      minWidth: 118,
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return '-';
        return (
          <span
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
            title={params.value?.startsWith('PRJ') ? 'Proje detayları' : 'Ürün detayları'}
            onClick={() => params.data && openItemView(params.data)}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.['rootCode'] || '-',
      minWidth: 88,
      flex: 0.6,
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 98,
      flex: 0.7,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Adı',
      field: 'itemName',
      minWidth: 200,
      flex: 2.5,
      tooltipField: 'itemName',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Miktar',
      field: 'requestedQuantity',
      minWidth: 100,
      flex: 0.8,
      type: 'rightAligned',
      valueFormatter: (params) => {
        if (!params.value) return '-';
        const unit = params.data?.unit || 'Adet';
        return `${Number(params.value).toLocaleString('tr-TR')} ${unit}`;
      },
    },
    {
      headerName: 'Teslim Tarihi',
      field: 'requestedDeliveryDate',
      minWidth: 128,
      flex: 0.9,
      valueFormatter: (params) => formatCellValue(params.value, 'date') as string,
    },
    {
      headerName: 'Not',
      valueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      minWidth: 100,
      flex: 1.2,
      tooltipValueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'İşlem',
      sortable: false,
      filter: false,
      resizable: false,
      width: 92,
      maxWidth: 100,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams) => (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => openEditModal(params.data)}
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 min-w-[28px] min-h-[28px] flex items-center justify-center"
            title="Düzenle"
          >
            <i className="fas fa-edit text-xs"></i>
          </button>
          <button
            onClick={() => params.data?.id && handleDeleteRequest(params.data.id)}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 min-w-[28px] min-h-[28px] flex items-center justify-center"
            title="Sil"
          >
            <i className="fas fa-trash text-xs"></i>
          </button>
        </div>
      ),
    },
  ], [openItemView, openEditModal, handleDeleteRequest, formatCellValue]);

  const productModalColumnDefs = useMemo<ColDef<Item>[]>(() => [
    {
      headerName: 'Ürün Kodu',
      field: 'itemCode',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.['rootCode'] || '-',
      minWidth: 80,
      flex: 0.6,
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 100,
      flex: 0.8,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Adı',
      field: 'itemName',
      minWidth: 250,
      flex: 2,
      tooltipField: 'itemName',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Kategori',
      field: 'categoryName',
      minWidth: 150,
      flex: 1.2,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Tip',
      field: 'itemType',
      minWidth: 100,
      flex: 0.8,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'MOQ',
      field: 'moq',
      minWidth: 80,
      flex: 0.5,
      type: 'rightAligned',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Birim',
      field: 'unit',
      minWidth: 80,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
  ], []);

  const handleProductRowClicked = useCallback((event: RowClickedEvent<Item>) => {
    if (event.data) {
      handleProductSelect(event.data);
    }
  }, [handleProductSelect]);

  return (
    <Layout currentScreen="create-request">
      <div className="max-w-8xl mx-auto h-full flex flex-col min-h-0">
        {/* Loading indicator */}
        {isLoading && (
          <div className="mb-4 flex items-center">
            <i className="fas fa-spinner fa-spin text-blue-600"></i>
          </div>
        )}

        {/* Evaluation sending loading panel */}
        {isSendingToEvaluation && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                <div className="text-blue-700 dark:text-blue-300 font-medium">
                  Talepler değerlendirmeye gönderiliyor...
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col flex-1 min-h-0 max-h-[calc(100vh-120px)] sm:max-h-[calc(100vh-140px)]">
            <div className="bg-gray-50 dark:bg-gray-700 px-3 sm:px-6 py-2 border-b border-gray-200 dark:border-gray-600 transition-colors duration-300 flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Talep Listesi 
                    {poRequests.length > 0 && (
                      <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                        ({poRequests.length} mevcut talep)
                      </span>
                    )}
                  </h3>
                  
                  {/* Product Selection Buttons - Moved to top left of grid */}
                  <div className="flex gap-3">
                    <button 
                      onClick={openProductModal}
                      className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                      title="Mevcut ürün kataloğundan seçin"
                    >
                      <i className="fas fa-search text-sm"></i>
                      <span>Ürün Ekle</span>
                    </button>
                    
                    <button 
                      onClick={() => setShowProjectModal(true)}
                      className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center gap-2"
                      title="Yeni proje/hizmet tanımla"
                    >
                      <i className="fas fa-plus text-sm"></i>
                      <span>Proje Ekle</span>
                    </button>
                    
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  {/* Action Buttons - Moved to top right of grid */}
                  {poRequests.length > 0 && (
                    <>
                      <button 
                        onClick={showSendToEvaluationConfirmation}
                        disabled={isLoading || isSendingToEvaluation || poRequests.length === 0}
                        className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                      >
                        {isSendingToEvaluation ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Gönderiliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Değerlendirmeye Gönder {poRequests.length > 0 && `(${poRequests.length})`}
                          </>
                        )}
                      </button>
                    </>
                  )}
                  {cartItems.length > 0 && (
                    <button 
                      onClick={submitRequest}
                      disabled={isLoading}
                      className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                    >
                      {isLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2"></i>
                          Yeni Talepleri Gönder ({cartItems.length})
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop AG Grid */}
            <div className="hidden lg:block flex-1 min-h-0">
              <BaseGrid
                gridStateKey="ag-grid-create-request"
                rowData={poRequests}
                columnDefs={columnDefs}
                loading={isLoading}
                getRowId={(params) => String(params.data.id)}
                domLayout="normal"
              />
            </div>

            {/* Mobile Card Layout */}
            <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto">
              {/* Loading State */}
              {isLoading && (
                <div className="px-3 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 dark:border-gray-700 mb-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">Talepler yükleniyor...</p>
                  </div>
                </div>
              )}
              
              {/* API'den gelen mevcut talepler */}
              {!isLoading && poRequests.map((item) => (
                <div key={`api-${item.id}`} className="px-3 sm:px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-2">
                          <i className="fas fa-file-alt text-blue-600 dark:text-blue-400 text-sm"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.itemName || 'Talep'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.itemCode || item.itemId}
                          </p>
                        </div>
                      </div>
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => openEditModal(item)}
                        className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 min-w-[32px] min-h-[32px] flex items-center justify-center"
                        title="Düzenle"
                      >
                        <i className="fas fa-edit text-sm"></i>
                      </button>
                      <button 
                        onClick={() => item.id && handleDeleteRequest(item.id)}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 min-w-[32px] min-h-[32px] flex items-center justify-center"
                        title="Sil"
                      >
                        <i className="fas fa-trash text-sm"></i>
                      </button>
                    </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kök Kod:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {item['rootCode'] || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kısa Kod:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {item.shortCode || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Açıklama:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-48" title={'-'}>
                          {'-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Miktar:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {item.requestedQuantity ? `${Number(item.requestedQuantity).toLocaleString('tr-TR')} ${item.unit || 'Adet'}` : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Teslim Tarihi:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {formatCellValue(item.requestedDeliveryDate, 'date')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Not:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-48" title={(item.note ?? item.Note) || ''}>
                          {(item.note ?? item.Note) || '-'}
                        </span>
                      </div>
                  </div>
                </div>
              ))}
              
              {/* Yeni eklenen sepet öğeleri */}
              {!isLoading && cartItems.map((item, index) => (
                <div key={index} className="px-3 sm:px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-2">
                          <i className="fas fa-box text-blue-600 dark:text-blue-400 text-sm"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{item.itemName}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.itemCode}</p>
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <button 
                          onClick={() => startEditItem(item, index)}
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Düzenle"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          onClick={() => removeFromCart(index)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kısa Kod:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{item.shortCode || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Açıklama:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{'-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Acil:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {item.isUrgent ? (
                            <span className="text-red-600 dark:text-red-400">Evet</span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400">Hayır</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Miktar:</span>
                        {editingItemId === index ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              min="1"
                              value={editQuantity ? Number(editQuantity).toLocaleString('tr-TR') : ''}
                              onChange={(e) => {
                                const rawValue = e.target.value.replace(/\./g, '');
                                const numValue = parseInt(rawValue) || 0;
                                if (numValue >= 0) {
                                  setEditQuantity(numValue);
                                }
                              }}
                              onFocus={(e) => e.target.select()}
                              onBlur={(e) => {
                                const rawValue = e.target.value.replace(/\./g, '');
                                const numValue = parseInt(rawValue) || 0;
                                if (numValue < 1) {
                                  setEditQuantity(1);
                                }
                              }}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400">{item.unit || 'Adet'}</span>
                            <button
                              onClick={saveEditItem}
                              className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 p-1"
                            >
                              <i className="fas fa-check text-xs"></i>
                            </button>
                            <button
                              onClick={cancelEditItem}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-1"
                            >
                              <i className="fas fa-times text-xs"></i>
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {item.requestedQuantity ? `${Number(item.requestedQuantity).toLocaleString('tr-TR')} ${item.unit || 'Adet'}` : '-'}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Teslim Tarihi:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {formatDateWithMoment(item.requestedDeliveryDate)}
                        </span>
                    </div>
                  </div>
                </div>
              ))}
              
              {!isLoading && poRequests.length === 0 && cartItems.length === 0 && (
                <div className="px-3 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <i className="fas fa-shopping-cart text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
                    <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">Henüz Talep Yok</h3>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Ürün veya proje eklemek için yukarıdaki butonları kullanın</p>
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Product Selection Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
                <div className="p-3 sm:p-4 md:p-6">
                  <div className="flex justify-between items-center mb-3 sm:mb-4 md:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Ürün Seçimi</h3>
                    <button 
                      onClick={closeProductModal}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <i className="fas fa-times text-xl"></i>
                    </button>
                  </div>
                  

                  {/* Column search bars - dar genişlik */}
                  <div className="mb-3 sm:mb-4 md:mb-6">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 mb-3">
                      <input 
                        ref={productSearchInputRef} 
                        type="text" 
                        placeholder="Ürün Kodu, Kısa Kod veya Adı ile Ara..." 
                        value={productModalForm.searchTerm} 
                        onChange={(e) => setProductModalForm(prev => ({ ...prev, searchTerm: e.target.value }))} 
                        onKeyPress={handleSearchKeyPress}
                        className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-xs focus:ring-2 focus:ring-blue-600" 
                      />
                      <select
                        value={productModalForm.selectedCategoryId || ''}
                        onChange={(e) => {
                          const newCategoryId = e.target.value ? parseInt(e.target.value) : null;
                          setProductModalForm(prev => ({ ...prev, selectedCategoryId: newCategoryId }));
                          // Kategori değiştiğinde otomatik arama yap
                          dispatch(resetPagination());
                          setCurrentPage(1);
                          const requestParams: any = { Page: 1, PageSize: 50 };
                          if (newCategoryId !== null) requestParams.CategoryId = newCategoryId;
                          if (productModalForm.searchTerm) requestParams.SearchKeyword = productModalForm.searchTerm;
                          dispatch(getItemsWithPagination(requestParams));
                        }}
                        className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-600 min-w-[140px]"
                      >
                        <option value="">Tüm Kategoriler</option>
                        {itemCategories?.map((category) => (
                          <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                      </select>
                      <button 
                        onClick={handleSearch} 
                        disabled={itemsLoading} 
                        className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        {itemsLoading ? (
                          <><i className="fas fa-spinner fa-spin mr-1"></i> Aranıyor...</>
                        ) : (
                          <><i className="fas fa-search mr-1"></i> Ara</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Product Grid */}
                  <div className="mb-3 sm:mb-4 md:mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {/* Desktop AG Grid */}
                      <div className="hidden lg:flex flex-col" style={{ height: 'clamp(250px, 35vh, 384px)' }}>
                        <div className="flex-1 min-h-0 relative">
                          <div className="absolute inset-0">
                            <BaseGrid<Item>
                              ref={productModalGridRef}
                              gridStateKey="ag-grid-create-request-product-modal"
                              rowData={allItems}
                              columnDefs={productModalColumnDefs}
                              loading={itemsLoading && !isLoadingMore}
                              onRowClicked={handleProductRowClicked}
                              getRowId={(params) => String(params.data.id)}
                              rowClassRules={{
                                'bg-blue-50 dark:bg-blue-900/20': (params) => params.data?.id === selectedProduct?.id,
                              }}
                              rowStyle={{ cursor: 'pointer' }}
                              domLayout="normal"
                            />
                          </div>
                        </div>
                        {pagination.hasMore && (
                          <div className="px-2 py-1.5 text-center border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 flex-shrink-0">
                            <button
                              onClick={loadMoreItems}
                              disabled={isLoadingMore}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            >
                              {isLoadingMore ? (
                                <>
                                  <i className="fas fa-spinner fa-spin text-[10px]"></i>
                                  Yükleniyor...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-plus text-[10px]"></i>
                                  Daha Fazla ({pagination.totalCount - allItems.length})
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Mobile Card Layout */}
                      <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                        {allItems.map((item) => {
                          const isSelected = selectedProduct?.id === item.id;
                          return (
                            <div 
                              key={item.id}
                              className={`px-3 sm:px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                                isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                              onClick={() => handleProductSelect(item)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex-1">
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                    {item.itemName || '-'}
                                  </h4>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {item.itemCode || '-'}
                                  </p>
                                </div>
                                {isSelected && (
                                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                                    <i className="fas fa-check-circle text-sm"></i>
                                  </div>
                                )}
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Kök Kod:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item['rootCode'] || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Kısa Kod:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item.shortCode || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Kategori:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item.categoryName || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tip:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item.itemType || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">MOQ:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item.moq || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Birim:</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300">{item.unit || '-'}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {(itemsLoading || isLoadingMore) && (
                          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                            <p>{isLoadingMore ? 'Daha fazla ürün yükleniyor...' : 'Ürünler yükleniyor...'}</p>
                          </div>
                        )}
                        
                        {!itemsLoading && allItems.length === 0 && (
                          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            <i className="fas fa-search text-2xl mb-2"></i>
                            <p>
                              {productModalForm.searchTerm ? (
                                <>Arama kriterlerinize uygun ürün bulunamadı.</>
                              ) : (
                                <>Arama kriterlerinize uygun ürün bulunamadı.</>
                              )}
                            </p>
                          </div>
                        )}
                        
                        {/* Load More Button */}
                        {pagination.hasMore && (
                          <div className="px-2 py-1.5 text-center border-t border-gray-200 dark:border-gray-600">
                            <button
                              onClick={loadMoreItems}
                              disabled={isLoadingMore}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            >
                              {isLoadingMore ? (
                                <>
                                  <i className="fas fa-spinner fa-spin text-[10px]"></i>
                                  Yükleniyor...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-plus text-[10px]"></i>
                                  Daha Fazla ({pagination.totalCount - allItems.length})
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Selected Product Info and Form */}
                  {selectedProduct && (
                    <div className="mb-2 px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700 flex items-center gap-2">
                      <span className="text-xs font-medium text-blue-900 dark:text-blue-100 whitespace-nowrap">Seçilen:</span>
                      <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{selectedProduct.itemName || '-'}</span>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">({selectedProduct.itemCode})</span>
                      <button
                        onClick={() => setSelectedProduct(null)}
                        className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                        title="Seçimi kaldır"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  )}
                  
                  {/* Quantity & Delivery Date */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Miktar {selectedProduct?.moq ? <span className="font-normal text-gray-400">(min: {selectedProduct.moq})</span> : null}
                      </label>
                      <input 
                        type="text" 
                        min={selectedProduct?.moq || 1}
                        value={productModalForm.quantity ? Number(productModalForm.quantity).toLocaleString('tr-TR') : ''}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/\./g, '');
                          const numValue = parseInt(rawValue) || 0;
                          if (numValue >= 0) {
                            setProductModalForm(prev => ({ ...prev, quantity: numValue }));
                          }
                        }}
                        onFocus={(e) => {
                          setTimeout(() => {
                            e.target.select();
                          }, 0);
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value.replace(/\./g, '');
                          const numValue = parseInt(rawValue) || 0;
                          if (numValue < (selectedProduct?.moq || 1)) {
                            setProductModalForm(prev => ({ ...prev, quantity: selectedProduct?.moq || 1 }));
                          }
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <DateInput
                        label="Teslim Tarihi"
                        labelClassName="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                        inputClassName="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        value={productModalForm.deliveryDate}
                        onChange={(value) => setProductModalForm(prev => ({ ...prev, deliveryDate: value }))}
                        min={getCurrentDateForInput()}
                        placeholder={getMomentDatePlaceholder()}
                        required={true}
                      />
                    </div>
                  </div>
                  {/* Note */}
                  <div className="mb-2 sm:mb-3">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Not <span className="font-normal text-gray-400">({productModalForm.note.length}/100)</span>
                    </label>
                    <input
                      type="text"
                      value={productModalForm.note}
                      onChange={(e) => setProductModalForm(prev => ({ ...prev, note: e.target.value.slice(0, 100) }))}
                      maxLength={100}
                      placeholder="Not ekleyin..."
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button 
                      onClick={closeProductModal}
                      className="px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm sm:text-base"
                    >
                      İptal
                    </button>
                    <button 
                      onClick={addProductAndContinue}
                      disabled={!selectedProduct}
                      className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                      style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}
                    >
                      <i className="fas fa-plus mr-1.5"></i>
                      Ekle ve Devam Et
                    </button>
                    <button 
                      onClick={addProductFromModal}
                      disabled={!selectedProduct}
                      className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      <i className="fas fa-check mr-1.5"></i>
                      Ekle
                    </button>
                  </div>
                </div>
              </div>
          </div>
        )}


        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Talep Düzenle</h3>
                    <button 
                      onClick={closeEditModal}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <i className="fas fa-times text-xl"></i>
                    </button>
                  </div>
                  
                  {itemToEdit && (
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Talep Bilgileri</h4>
                        <div className="space-y-1 text-sm">
                          <div><span className="font-medium text-gray-700 dark:text-gray-300">Ürün:</span> <span className="text-gray-900 dark:text-white">{itemToEdit.itemName}</span></div>
                          <div><span className="font-medium text-gray-700 dark:text-gray-300">Kod:</span> <span className="text-gray-900 dark:text-white">{itemToEdit.itemCode}</span></div>
                          <div><span className="font-medium text-gray-700 dark:text-gray-300">MOQ:</span> <span className="text-gray-900 dark:text-white">{selectedItem?.moq || itemToEdit.moq || '-'}</span></div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Miktar
                          </label>
                          <input 
                            ref={editModalQuantityInputRef}
                            type="text" 
                            min={selectedItem?.moq || itemToEdit?.moq || 1}
                            value={editModalForm.quantity ? Number(editModalForm.quantity).toLocaleString('tr-TR') : ''}
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/\./g, '');
                              const numValue = parseInt(rawValue) || 0;
                              const minValue = selectedItem?.moq || itemToEdit?.moq || 1;
                              if (numValue >= minValue) {
                                setEditModalForm(prev => ({ ...prev, quantity: numValue }));
                              }
                            }}
                            onFocus={(e) => {
                              setTimeout(() => {
                                e.target.select();
                              }, 0);
                            }}
                            onBlur={(e) => {
                              const rawValue = e.target.value.replace(/\./g, '');
                              const numValue = parseInt(rawValue) || 0;
                              const minValue = selectedItem?.moq || itemToEdit?.moq || 1;
                              if (numValue < minValue) {
                                setEditModalForm(prev => ({ ...prev, quantity: minValue }));
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          {(selectedItem?.moq || itemToEdit?.moq) && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Minimum sipariş miktarı: {selectedItem?.moq || itemToEdit.moq}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <DateInput
                            label="Teslim Tarihi"
                            value={editModalForm.deliveryDate}
                            onChange={(value) => setEditModalForm(prev => ({ ...prev, deliveryDate: value }))}
                            min={getCurrentDateForInput()}
                            placeholder={getMomentDatePlaceholder()}
                            required={true}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Not
                        </label>
                        <textarea
                          value={editModalForm.note}
                          onChange={(e) => setEditModalForm(prev => ({ ...prev, note: e.target.value.slice(0, 100) }))}
                          maxLength={100}
                          rows={3}
                          placeholder="Ürün ile ilgili not..."
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                          {editModalForm.note.length}/100
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button 
                      onClick={closeEditModal}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      İptal
                    </button>
                    <button 
                      onClick={handleEditSubmit}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <i className="fas fa-save mr-2"></i>
                      Güncelle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Modal */}
        {showProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-auto my-4 transition-colors duration-300 relative max-h-[calc(100vh-2rem)]">
              {/* Minimal Header */}
              <div className="h-4 flex items-start justify-end px-4 pt-1 flex-shrink-0">
                <button 
                  onClick={() => setShowProjectModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(100vh-6rem)] flex flex-col">
                  <form className="space-y-6">
                    {/* 1. Proje Kimliği - En önemli bilgi */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Proje Başlığı
                        </label>
                        <input 
                          type="text" 
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Örn: Dünya küresi geliştirme projesi"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Proje Detayları
                        </label>
                        <textarea 
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Proje hakkında detaylı bilgi verin. İhtiyaçlarınızı, beklentilerinizi ve özel gereksinimlerinizi açıklayın..."
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Tip
                          </label>
                          <input 
                            type="text" 
                            value="Proje"
                            readOnly
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Proje Kategorisi
                          </label>
                          <select className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Kategori seçiniz</option>
                            {categoriesLoading ? (
                              <option value="" disabled>Kategoriler yükleniyor...</option>
                            ) : itemCategories && itemCategories.length > 0 ? (
                              itemCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>Kategori bulunamadı</option>
                            )}
                          </select>
                        </div>
                        
                        <div>
                          <DateInput
                            label="İstenilen Teslim Tarihi"
                            value={projectModalForm.deliveryDate}
                            onChange={(value) => setProjectModalForm(prev => ({ ...prev, deliveryDate: value }))}
                            min={getCurrentDateForInput()}
                            placeholder={getMomentDatePlaceholder()}
                            required={true}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 2. Miktar Bilgisi - Ne kadar istiyor */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Miktar
                          </label>
                          <input 
                            type="text" 
                            min="1"
                            value={projectModalForm.quantity ? Number(projectModalForm.quantity).toLocaleString('tr-TR') : ''}
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/\./g, '');
                              const numValue = parseFloat(rawValue) || 0;
                              if (numValue >= 0) {
                                setProjectModalForm(prev => ({ ...prev, quantity: numValue }));
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) => {
                              const rawValue = e.target.value.replace(/\./g, '');
                              const numValue = parseFloat(rawValue) || 0;
                              if (numValue < 1) {
                                setProjectModalForm(prev => ({ ...prev, quantity: 1 }));
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Miktar giriniz"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Birim
                          </label>
                          <input 
                            type="text" 
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Birim giriniz (örn: Adet, Kilogram, Metre)"
                            pattern="[a-zA-ZğüşıöçĞÜŞİÖÇ\s]*"
                            onKeyPress={(e) => {
                              if (!/[a-zA-ZğüşıöçĞÜŞİÖÇ\s]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            KDV Oranı (%)
                          </label>
                          <input 
                            type="number" 
                            name="vatRate"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>


                    {/* 4. Yüklenen Dosyalar */}
                    <div className="space-y-4">
                      {projectModalForm.attachments.length > 0 && (
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Yüklenen Dosyalar:</h5>
                          <div className="max-h-32 overflow-y-auto">
                            <div className="grid grid-cols-1 gap-1">
                              {projectModalForm.attachments.map((file, index) => (
                                <div key={`file-${index}`} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded text-xs">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <i className="fas fa-image text-blue-600 flex-shrink-0"></i>
                                    <div className="flex-1 min-w-0">
                                      <div className="truncate text-gray-700 dark:text-gray-300 font-medium">
                                        {file.name}
                                      </div>
                                      <div className="text-gray-500 dark:text-gray-400">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                  </div>
                                  <button 
                                    type="button"
                                    onClick={() => removeAttachment(index)}
                                    className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0 p-1"
                                    title="Dosyayı kaldır"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 5. Ek Belgeler - Kanıt/Referans */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Görsel Ekle
                      </h4>
                      
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer">
                        <input 
                          type="file" 
                          id="project-file-upload" 
                          multiple 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleFileUpload}
                        />
                        <div onClick={() => document.getElementById('project-file-upload')?.click()}>
                          <i className="fas fa-image text-4xl text-gray-400 dark:text-gray-500 mb-3"></i>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Görselleri buraya sürükleyin veya tıklayın
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG, JPEG dosyaları desteklenmektedir
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                  
                  {/* Footer Buttons */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600 mt-6 flex-shrink-0">
                    <button 
                      onClick={() => {
                        setShowProjectModal(false);
                        resetProjectModalForm();
                      }}
                      className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                    >
                      İptal
                    </button>
                    <button 
                      onClick={handleProjectSubmit}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center"
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Listeye Ekle
                    </button>
                  </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Edit Modal */}
        {showProjectEditModal && projectToEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-auto">
              <ProjectEditView
                projectData={{
                  id: projectToEdit.id?.toString() || '',
                  requestedDate: projectEditDeliveryDate,
                  quantity: projectToEdit.requestedQuantity || 1,
                  itemId: projectToEdit.itemId,
                  itemName: projectToEdit.itemName,
                  itemCode: projectToEdit.itemCode,
                  shortCode: projectToEdit.shortCode,
                  categoryId: projectToEdit.categoryId,
                  categoryName: projectToEdit.categoryName,
                  attachmentUrl: projectToEdit.attachmentUrl,
                  isUrgent: projectToEdit.isUrgent,
                  requestStatus: projectToEdit.requestStatus,
                  approvedQuantity: projectToEdit.approvedQuantity
                }}
                deliveryDate={projectEditDeliveryDate}
                onDeliveryDateChange={setProjectEditDeliveryDate}
                onCancel={closeProjectEditModal}
                onSave={fetchPORequests}
              />
            </div>
          </div>
        )}


        {/* ItemView Modal */}
        {showItemViewModal && itemToView && (
          <ItemView
            isOpen={showItemViewModal}
            onClose={closeItemViewModal}
            itemId={itemToView.itemId}
            mode="view"
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={cancelAction}
          onConfirm={confirmAction}
          title={
            confirmationType === 'deleteRequest' ? "Talep Sil" : 
            confirmationType === 'deleteAllRequests' ? "Tüm Talepleri Sil" : 
            confirmationType === 'sendToEvaluation' ? "Değerlendirmeye Gönder" :
            "Ürünü Sil"
          }
          message={
            confirmationType === 'deleteRequest' 
              ? "Bu talebi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz." 
              : confirmationType === 'deleteAllRequests'
              ? "Tüm talepleri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
              : confirmationType === 'sendToEvaluation'
              ? "Tüm talepleri değerlendirmeye göndermek istediğinizden emin misiniz? Bu işlemden sonra talepler değerlendirme sürecine girecektir."
              : "Bu ürünü sepetten kaldırmak istediğinizden emin misiniz?"
          }
          confirmText={confirmationType === 'sendToEvaluation' ? "Gönder" : "Sil"}
          cancelText="İptal"
          type={confirmationType === 'sendToEvaluation' ? "success" : "danger"}
          icon={confirmationType === 'sendToEvaluation' ? "fas fa-check-circle" : "fas fa-trash"}
        />
      </div>
    </Layout>
  );
};

export default CreateRequestPage;
